import React from "react";
import PropTypes from "prop-types";
import { connect } from "formik";
import classNames from "classnames";
import traverse from "traverse";

import {
  Input as ReactStrapInput
} from "reactstrap";

import ReactSelect from "react-select";
import SharedDropzone from "shared/react_components/Dropzone/Dropzone";

import pipe from "shared/pipe";

import errorsForField from "./errorsForField";
import withErrors from "./withErrors";

import { findOption, findOptionWhenGrouped, DEFAULT_SELECT_VALUE } from "shared/utils/select_helpers";

export const DataBag = connect(({ children, formik }) => children(formik));

class ObserveImpl extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    property: PropTypes.string.isRequired,
    formik: PropTypes.object.isRequired
  };

  componentDidUpdate({ formik: prevFormik }) {
    if (prevFormik[this.props.property] !== this.props.formik[this.props.property]) {
      this.props.onChange(prevFormik, this.props.formik);
    }
  }

  render() {
    return null;
  }
}

export const Observe = connect(ObserveImpl);

export function addListFormikAdapter({ field, form, fieldNames, requireTouched }) {
  return ({ item, fieldUpdater, index }) => {
    const transformedProps = {};

    fieldNames.forEach((fieldName) => {
      const qualifiedName = `${field.name}[${index}].${fieldName}`;

      transformedProps[fieldName] = {
        name: qualifiedName,
        value: item[fieldName],
        errors: errorsForField(form, qualifiedName, { requireTouched }),
        onChange: fieldUpdater(fieldName),
        onBlur: (e) => form.handleBlur(e)
      };
    });

    return transformedProps;
  };
}

export const Input = pipe(
  withErrors,
  ({ field, form, fieldErrors, ...props }) => <ReactStrapInput {...field} {...props}/>
);

export function Checkbox(props) {
  return (
    <Input
      {...props}
      type='checkbox'
      checked={props.field.value}
      onChange={({ target: { checked } }) => {
        props.form.setFieldValue(props.field.name, checked);
        props.onChange(props.form, props.field.name, checked);
      }}
    />
  );
}

Checkbox.propTypes = {
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  onChange: PropTypes.func
};

Checkbox.defaultProps = {
  onChange: () => {}
};

export function CheckboxWithLabel({ field, form, label, ...props }) {
  return (
    <div className='custom-control custom-checkbox'>
      <Checkbox field={field} form={form} className='custom-control-input' id={field.name} {...props} />
      <label className='custom-control-label' htmlFor={field.name}>{label}</label>
    </div>
  );
}

CheckboxWithLabel.propTypes = {
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired
};

export function RadioButton(props) {
  const field = props.field;

  return (
    <Input
      {...props}
      type='radio'
      checked={field.value === props.value}
    />
  );
}

RadioButton.propTypes = {
  field: PropTypes.any.isRequired,
  value: PropTypes.string.isRequired
};

export const Select = pipe(withErrors, ({ options, field, form, fieldErrors, ...props }) => (
  <ReactSelect
    options={options}
    classNamePrefix="react-select"
    className={classNames({
      "react-select": true,
      "is-invalid": field.invalid
    })}
    name={field.name}
    value={options
      ? props.grouped
        ? findOptionWhenGrouped(options, field.value)
        : findOption(options, field.value)
      : DEFAULT_SELECT_VALUE
    }
    onChange={(option) => form.setFieldValue(field.name, option ? option.value : DEFAULT_SELECT_VALUE)}
    onBlur={field.onBlur}
    styles={{ menu: base => ({ ...base, zIndex: 999 }) }}
    {...props}
  />
));

Select.propTypes = {
  options: PropTypes.any.isRequired,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  grouped: PropTypes.bool
};

Select.defaultProps = {
  grouped: false
};

export const Dropzone = pipe(withErrors, ({ field: { name, value }, form: { setFieldValue }, ...props }) => (
  <SharedDropzone
    name={name}
    value={value}
    onChange={files => setFieldValue(name, files)}
    {...props}
  />
));

Dropzone.propTypes = {
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired
};

export function Debug() {
  return (
    <DataBag>
      {(bag) => (
        <pre>{JSON.stringify(bag, null, 2)}</pre>
      )}
    </DataBag>
  );
}

function convertTraversePathToLodashPath(traversePath) {
  return traversePath.reduce((acc, pathPart) => {
    if (pathPart.match(/\d+/)) {
      return `${acc}[${pathPart}]`;
    } else if (acc.length === 0) {
      return pathPart;
    } else {
      return `${acc}.${pathPart}`;
    }
  }, "");
}

export function unchangedFields(prevValues, values) {
  const prevValuesTraverser = traverse(prevValues);

  return traverse(values).reduce(function(acc, value) {
    if (this.isLeaf) {
      const prevValue = prevValuesTraverser.get(this.path);

      if (prevValue === value) {
        acc.push(convertTraversePathToLodashPath(this.path));
      }
    }

    return acc;
  }, []);
}
